// Libellés français des outils du serveur MCP des outils métier (« astraly-outils-metier »,
// affiché « Outils pirouette » dans les conversations) : sans eux, les étapes d'un message se
// lisent « Outils pirouette browser read » (nom du serveur + nom brut de l'outil).
// Pas de tiret long ni de nom de marque dans un texte affiché.
import { t } from "@app/lib/compta/i18n";
import { asDisplayName, slugify } from "@app/types/shared/utils/string_utils";

const LIBELLE_PAR_OUTIL: Record<string, string> = {
  browser_open: "Navigateur : ouvrir une page",
  browser_read: "Navigateur : lire la page",
  browser_click: "Navigateur : cliquer",
  browser_type: "Navigateur : saisir",
  browser_login: "Navigateur : se connecter",
  lister_comptes_rendus: "Lister les comptes rendus",
  lire_compte_rendu: "Lire un compte rendu",
  envoyer_compte_rendu: "Envoyer un compte rendu",
  notifier_collegue: "Prévenir un collègue",
  mailbox_list_accounts: "Boîte mail : comptes",
  mailbox_search: "Boîte mail : rechercher",
  mailbox_read: "Boîte mail : lire",
  mailbox_send: "Boîte mail : envoyer",
  quiz_campagnes: "Campagnes de quiz",
  quiz_bibliotheque: "Bibliothèque de quiz",
  quiz_lire: "Lire un quiz",
  webinaires: "Webinaires",
  equipe: "Équipe",
};

// Electron seulement (sous-plan 12) : Boosts sert aussi view_chantiers et garde son libellé
// d'origine. Servi dans la SPA d'Electron (NEXT_PUBLIC_MARQUE=electron) ou sous l'action
// « outils_electron ». Boîte mail et navigateur sont déjà traduits ci-dessus.
const LIBELLE_PAR_OUTIL_ELECTRON: Record<string, string> = {
  view_chantiers: "Chantiers",
};

function estOutilsElectron(serveur: string): boolean {
  return (
    process.env.NEXT_PUBLIC_MARQUE === "electron" ||
    slugify(serveur) === "outils_electron"
  );
}

// Pennylane (Electron, sous-plan 12) : serveur MCP distant à part, en lecture seule, dont les
// outils portent tous le préfixe « pennylane_ ». Seuls les principaux sont traduits ; les autres
// gardent le comportement d'origine.
const LIBELLE_PAR_OUTIL_PENNYLANE: Record<string, string> = {
  pennylane_invoices_list: "Pennylane : factures clients",
  pennylane_customers_list: "Pennylane : clients",
  pennylane_supplier_invoices_list: "Pennylane : factures fournisseurs",
  pennylane_suppliers_list: "Pennylane : fournisseurs",
  pennylane_bank_accounts_list: "Pennylane : comptes bancaires",
  pennylane_transactions_list: "Pennylane : transactions bancaires",
  pennylane_fiscal_years_list: "Pennylane : exercices comptables",
  pennylane_trial_balance_get: "Pennylane : balance",
  pennylane_ledger_accounts_list: "Pennylane : plan comptable",
  pennylane_ledger_entry_lines_list: "Pennylane : écritures comptables",
};

/** Vrai pour le serveur Pennylane (nom du serveur ou de son action, « pennylane », « Pennylane »). */
function estServeurPennylane(serveur: string): boolean {
  return /^pennylane(_|$)/.test(slugify(serveur));
}

const SEPARATEUR = "__";

/**
 * Vrai pour le serveur des outils métier, quel que soit le nom sous lequel il arrive : nom du
 * serveur (« astraly-outils-metier ») ou nom affiché de sa vue (« Outils pirouette »), bruts ou
 * en préfixe d'un nom d'appel (« outils_pirouette »).
 */
export function estServeurOutilsMetier(
  serveur: string | null | undefined
): boolean {
  if (!serveur) {
    return false;
  }
  const s = slugify(serveur);
  return s.startsWith("astraly_outils") || /^outils(_|$)/.test(s);
}

/**
 * Libellé français d'un outil métier connu (outils métier de l'app, ou Pennylane), ou null (on
 * garde alors le libellé d'origine).
 */
export function libelleOutilMetier(
  serveur: string | null | undefined,
  outil: string | null | undefined
): string | null {
  if (!outil || !serveur) {
    return null;
  }
  const tables = estServeurOutilsMetier(serveur)
    ? estOutilsElectron(serveur)
      ? [LIBELLE_PAR_OUTIL, LIBELLE_PAR_OUTIL_ELECTRON]
      : [LIBELLE_PAR_OUTIL]
    : estServeurPennylane(serveur)
      ? [LIBELLE_PAR_OUTIL_PENNYLANE]
      : [];
  const table = tables.find((t) =>
    Object.prototype.hasOwnProperty.call(t, outil)
  );
  return table ? table[outil] : null;
}

/** Même chose depuis un nom d'appel préfixé (« outils_pirouette__browser_open »). */
export function libelleAppelOutilMetier(
  nomAppel: string | null | undefined
): string | null {
  if (!nomAppel) {
    return null;
  }
  const i = nomAppel.lastIndexOf(SEPARATEUR);
  if (i === -1) {
    return null;
  }
  const serveur = nomAppel.slice(0, i);
  const outil = nomAppel.slice(i + SEPARATEUR.length);
  // Un serveur imbriqué (« sous_agent__outils_pirouette ») garde son dernier segment.
  const j = serveur.lastIndexOf(SEPARATEUR);
  const dernier = j === -1 ? serveur : serveur.slice(j + SEPARATEUR.length);
  return (
    libelleOutilMetier(serveur, outil) ?? libelleOutilMetier(dernier, outil)
  );
}

/** Forme attendue par les étapes d'un message (en cours et terminé). */
export function libellesAffichageOutilMetier(
  serveur: string | null | undefined,
  outil: string | null | undefined
): { running: string; done: string } | null {
  const libelle = libelleOutilMetier(serveur, outil);
  return libelle ? { running: libelle, done: libelle } : null;
}

// Outils des serveurs internes Gmail et Google Agenda (02/10) : la fiche d'un outil (« Outils et
// validations »), les étapes d'un message et la carte de validation montraient le nom brut de
// l'outil (« Get drafts ») et sa description anglaise. On traduit à l'AFFICHAGE seulement : les
// noms et descriptions envoyés au modèle restent ceux de l'amont (metadata.ts des serveurs).
// Un outil absent de la table garde le comportement d'origine.

// 08/10 : Google Drive personnel de chaque membre (serveur interne google_drive) traduit de même.

export type ServeurGoogle = "gmail" | "google_calendar" | "google_drive";

export const NOM_SERVEUR_GOOGLE: Record<ServeurGoogle, string> = {
  gmail: "Gmail",
  google_calendar: "Google Agenda",
  google_drive: "Google Drive",
};

export interface OutilTraduit {
  nom: string;
  description: string;
}

export const OUTILS_GOOGLE_FR: Record<
  ServeurGoogle,
  Record<string, OutilTraduit>
> = {
  gmail: {
    get_drafts: {
      nom: "Lister les brouillons",
      description:
        "Récupère et liste les brouillons enregistrés et non envoyés de Gmail. Renvoie uniquement les brouillons existants.",
    },
    create_draft: {
      nom: "Créer un brouillon",
      description:
        "Crée un nouveau brouillon dans Gmail, ou un brouillon de réponse à un message existant. Le brouillon est enregistré dans le compte Gmail de l'utilisateur, avec les en-têtes et la mise en forme adaptés, pour être relu et envoyé plus tard.",
    },
    delete_draft: {
      nom: "Supprimer un brouillon",
      description: "Supprime un brouillon de Gmail.",
    },
    get_messages: {
      nom: "Lire les e-mails",
      description:
        "Récupère les messages de la boîte de réception Gmail. Accepte les recherches Gmail pour filtrer les messages.",
    },
    get_attachment: {
      nom: "Télécharger une pièce jointe",
      description:
        "Télécharge un fichier joint à un e-mail Gmail. Le contenu de la pièce jointe est ensuite mis à disposition dans la conversation.",
    },
    get_labels: {
      nom: "Lister les libellés",
      description:
        "Récupère tous les libellés Gmail, ceux du système comme ceux créés par l'utilisateur.",
    },
    set_message_labels: {
      nom: "Modifier les libellés d'un e-mail",
      description:
        "Modifie les libellés d'un message Gmail pour le marquer comme lu ou non lu, l'étoiler, l'archiver, ou le placer dans la boîte de réception ou l'en retirer. Les libellés système (INBOX, SPAM, TRASH, UNREAD, STARRED, IMPORTANT…) s'utilisent directement ; ceux de l'utilisateur se récupèrent d'abord avec « Lister les libellés ».",
    },
    send_mail: {
      nom: "Envoyer un e-mail",
      description:
        "Envoie un e-mail directement depuis Gmail, immédiatement et sans créer de brouillon. À utiliser quand toutes les informations nécessaires sont réunies. L'e-mail part avec les en-têtes et la mise en forme adaptés.",
    },
    get_thread: {
      nom: "Lire une conversation",
      description: "Récupère tous les messages d'une conversation Gmail.",
    },
  },
  google_calendar: {
    list_calendars: {
      nom: "Lister les agendas",
      description:
        "Liste tous les agendas Google accessibles à l'utilisateur, page par page.",
    },
    list_events: {
      nom: "Lister les événements",
      description:
        "Liste, recherche ou parcourt les événements d'un agenda Google sur un jour, une semaine ou une période donnée. Avec un texte de recherche, cherche librement dans les événements.",
    },
    get_event: {
      nom: "Consulter un événement",
      description:
        "Récupère le détail complet d'un événement d'un agenda Google à partir de son identifiant.",
    },
    create_event: {
      nom: "Créer un événement",
      description:
        "Crée et planifie une réunion, un événement ou un rendez-vous dans Google Agenda. Par défaut, ajoute l'utilisateur comme organisateur et participant, et vérifie d'abord les disponibilités et les fuseaux horaires des participants.",
    },
    update_event: {
      nom: "Modifier un événement",
      description:
        "Modifie ou replanifie un événement ou une réunion de Google Agenda : nouvel horaire, lieu, participants ou visioconférence.",
    },
    delete_event: {
      nom: "Supprimer un événement",
      description: "Supprime ou annule un événement d'un agenda Google.",
    },
    check_availability: {
      nom: "Vérifier les disponibilités",
      description:
        "Trouve les créneaux où tous les participants sont libres, en croisant leurs disponibilités dans Google Agenda sur une période donnée.",
    },
    get_user_timezones: {
      nom: "Consulter les fuseaux horaires",
      description:
        "Récupère le fuseau horaire de plusieurs participants depuis les réglages de leur Google Agenda. Ne fonctionne que pour les agendas partagés avec vous.",
    },
  },
  google_drive: {
    list_drives: {
      nom: "Lister les Drive partagés",
      description:
        "Liste tous les Drive partagés de Google Drive auxquels l'utilisateur a accès.",
    },
    search_files: {
      nom: "Rechercher des fichiers",
      description:
        "Cherche des fichiers dans Google Drive par leur nom ou leur contenu : documents Google Docs, feuilles Google Sheets, présentations Google Slides et dossiers, dans le Drive personnel, tous les Drive partagés ou un Drive précis.",
    },
    get_file_content: {
      nom: "Lire un fichier",
      description:
        "Ouvre et lit le contenu d'un fichier Google Drive, page par page. Les Docs et Slides sont lus en texte, les Sheets au format Excel, les autres fichiers (PDF, Office, images) sont récupérés tels quels et joints à la conversation.",
    },
    get_document_structure: {
      nom: "Lire la structure d'un document",
      description:
        "Récupère la structure complète d'un document Google Docs : texte, titres, en-têtes, pieds de page, tableaux et positions des éléments.",
    },
    get_presentation_structure: {
      nom: "Lire la structure d'une présentation",
      description:
        "Récupère la structure complète d'une présentation Google Slides : diapositives, formes, tableaux, textes et identifiants des éléments.",
    },
    get_spreadsheet: {
      nom: "Consulter un classeur",
      description:
        "Récupère les informations d'un classeur Google Sheets : noms et identifiants des feuilles, nombre de lignes et de colonnes. Ne renvoie pas le contenu des cellules.",
    },
    get_worksheet: {
      nom: "Lire des cellules",
      description:
        "Lit les valeurs d'une plage de cellules d'une feuille Google Sheets, en valeurs affichées, brutes ou en formules.",
    },
    list_file_permissions: {
      nom: "Voir qui a accès à un fichier",
      description:
        "Liste les partages d'un fichier Google Drive : qui y a accès et avec quel rôle. Demande un droit de partage sur le fichier.",
    },
    list_comments: {
      nom: "Lister les commentaires",
      description:
        "Liste les commentaires d'un fichier Google Drive (document, classeur ou présentation), avec leurs réponses.",
    },
    create_document: {
      nom: "Créer un document",
      description:
        "Crée un nouveau document Google Docs, si besoin dans un dossier précis.",
    },
    create_spreadsheet: {
      nom: "Créer un classeur",
      description:
        "Crée un nouveau classeur Google Sheets, si besoin dans un dossier précis.",
    },
    create_presentation: {
      nom: "Créer une présentation",
      description:
        "Crée une nouvelle présentation Google Slides, si besoin dans un dossier précis.",
    },
    create_folder: {
      nom: "Créer un dossier",
      description:
        "Crée un nouveau dossier dans Google Drive, si besoin dans un dossier parent précis.",
    },
    copy_file: {
      nom: "Dupliquer un fichier",
      description:
        "Fait une copie d'un fichier Google Drive (document, classeur ou présentation) sous un nouveau nom, au même endroit ou ailleurs. Utile pour repartir d'un modèle en gardant sa mise en forme.",
    },
    create_comment: {
      nom: "Ajouter un commentaire",
      description:
        "Ajoute un commentaire sur un fichier Google Drive (document, classeur ou présentation).",
    },
    create_reply: {
      nom: "Répondre à un commentaire",
      description:
        "Répond à un commentaire existant sur un fichier Google Drive (document, classeur ou présentation).",
    },
    update_document: {
      nom: "Modifier un document",
      description:
        "Modifie un document Google Docs : remplacer, insérer, supprimer ou mettre en forme du texte, modifier des tableaux, des en-têtes et des pieds de page.",
    },
    append_to_spreadsheet: {
      nom: "Ajouter des lignes à un classeur",
      description:
        "Ajoute des lignes à la suite des données d'un classeur Google Sheets.",
    },
    update_spreadsheet: {
      nom: "Modifier un classeur",
      description:
        "Modifie un classeur Google Sheets : écrire des cellules, mettre en forme, rechercher et remplacer, fusionner, trier, insérer ou supprimer des lignes, des colonnes et des feuilles.",
    },
    update_presentation: {
      nom: "Modifier une présentation",
      description:
        "Modifie une présentation Google Slides : remplacer du texte, modifier des tableaux et des notes, ajouter ou supprimer des diapositives et des éléments.",
    },
    share_file: {
      nom: "Partager un fichier",
      description:
        "Partage un fichier Google Drive avec une personne (par son adresse e-mail), un groupe ou tout un domaine Google Workspace.",
    },
    update_file_permission: {
      nom: "Modifier un accès",
      description:
        "Change le rôle d'une personne ou d'un groupe qui a déjà accès à un fichier Google Drive.",
    },
    revoke_file_sharing: {
      nom: "Retirer un accès",
      description:
        "Arrête le partage d'un fichier Google Drive avec une personne ou un domaine.",
    },
    upload_file: {
      nom: "Déposer un fichier dans le Drive",
      description:
        "Dépose dans Google Drive un fichier de la conversation, si besoin dans un dossier précis.",
    },
  },
};

/**
 * Le serveur Google (Gmail, Google Agenda, Google Drive) sous l'un de ses noms : nom interne
 * (« gmail », « google_calendar », « google_drive ») ou nom affiché (« Google Agenda »).
 */
export function serveurGoogle(
  serveur: string | null | undefined
): ServeurGoogle | null {
  if (!serveur) {
    return null;
  }
  const s = slugify(serveur);
  if (s === "gmail") {
    return "gmail";
  }
  if (s === "google_calendar" || s === "google_agenda") {
    return "google_calendar";
  }
  if (s === "google_drive") {
    return "google_drive";
  }
  return null;
}

/** Nom et description français d'un outil Gmail ou Google Agenda connu, sinon null. */
export function outilGoogleTraduit(
  serveur: string | null | undefined,
  outil: string | null | undefined
): OutilTraduit | null {
  const s = serveurGoogle(serveur);
  if (!s || !outil) {
    return null;
  }
  return Object.prototype.hasOwnProperty.call(OUTILS_GOOGLE_FR[s], outil)
    ? OUTILS_GOOGLE_FR[s][outil]
    : null;
}

/**
 * Nom d'un outil tel qu'affiché à un membre : français pour un outil métier ou Google connu,
 * sinon le nom humanisé de l'amont.
 */
export function nomOutilAffiche(
  serveur: string | null | undefined,
  outil: string
): string {
  return (
    libelleOutilMetier(serveur, outil) ??
    outilGoogleTraduit(serveur, outil)?.nom ??
    // 08/10 : un outil sans libellé métier passe par la traduction de l'app (sinon nom humanisé).
    t(asDisplayName(outil))
  );
}

/** Description d'un outil telle qu'affichée : française pour un outil Google connu, sinon l'origine. */
export function descriptionOutilAffichee(
  serveur: string | null | undefined,
  outil: string,
  descriptionOrigine: string | null | undefined
): string | null {
  return (
    outilGoogleTraduit(serveur, outil)?.description ??
    descriptionOrigine ??
    null
  );
}

const LONGUEUR_MAX_DETAIL = 60;

function tronquer(texte: string): string {
  return texte.length > LONGUEUR_MAX_DETAIL
    ? texte.slice(0, LONGUEUR_MAX_DETAIL) + "…"
    : texte;
}

function texteNonVide(valeur: unknown): string | null {
  return typeof valeur === "string" && valeur.length > 0 ? valeur : null;
}

function destinataires(to: unknown): string {
  if (!Array.isArray(to) || !to.every((v) => typeof v === "string")) {
    return "";
  }
  if (to.length === 1) {
    return ` à ${tronquer(to[0])}`;
  }
  return to.length > 1 ? " à plusieurs destinataires" : "";
}

function actionDetaillee(
  serveur: ServeurGoogle,
  outil: string,
  entrees: Record<string, unknown>
): string | null {
  if (serveur === "gmail") {
    const q = texteNonVide(entrees.q);
    if ((outil === "get_drafts" || outil === "get_messages") && q) {
      return `rechercher « ${tronquer(q)} »`;
    }
    const sujet = texteNonVide(entrees.subject);
    if (outil === "create_draft" && sujet) {
      return `créer le brouillon « ${tronquer(sujet)} »${destinataires(entrees.to)}`;
    }
    if (outil === "send_mail" && sujet) {
      return `envoyer l'e-mail « ${tronquer(sujet)} »${destinataires(entrees.to)}`;
    }
    if (outil === "delete_draft" && sujet) {
      return `supprimer le brouillon « ${tronquer(sujet)} »`;
    }
    const fichier = texteNonVide(entrees.filename);
    if (outil === "get_attachment" && fichier) {
      return `télécharger la pièce jointe « ${tronquer(fichier)} »`;
    }
    return null;
  }
  if (serveur === "google_drive") {
    const q = texteNonVide(entrees.q);
    if (outil === "search_files" && q) {
      return `rechercher « ${tronquer(q)} »`;
    }
    const nom = texteNonVide(entrees.title) ?? texteNonVide(entrees.name);
    if (
      (outil === "create_document" ||
        outil === "create_spreadsheet" ||
        outil === "create_presentation" ||
        outil === "create_folder") &&
      nom
    ) {
      return `créer « ${tronquer(nom)} »`;
    }
    const courriel = texteNonVide(entrees.emailAddress);
    if (outil === "share_file" && courriel) {
      return `partager un fichier avec ${tronquer(courriel)}`;
    }
    return null;
  }
  const titre = texteNonVide(entrees.summary);
  if (outil === "create_event" && titre) {
    return `créer l'événement « ${tronquer(titre)} »`;
  }
  if (outil === "update_event" && titre) {
    return `modifier l'événement « ${tronquer(titre)} »`;
  }
  return null;
}

function minusculeInitiale(s: string): string {
  return s.charAt(0).toLocaleLowerCase("fr") + s.slice(1);
}

/**
 * Libellé d'une étape de message pour un outil Gmail ou Google Agenda connu : « Gmail : lister
 * les brouillons », précisé par les paramètres là où l'amont le fait (objet, destinataire,
 * recherche, titre de l'événement). Null pour un outil inconnu (comportement d'origine).
 */
export function libellesAffichageOutilGoogle(
  serveur: string | null | undefined,
  outil: string | null | undefined,
  entrees?: Record<string, unknown> | null
): { running: string; done: string } | null {
  const s = serveurGoogle(serveur);
  const traduit = outilGoogleTraduit(serveur, outil);
  if (!s || !outil || !traduit) {
    return null;
  }
  const action =
    actionDetaillee(s, outil, entrees ?? {}) ?? minusculeInitiale(traduit.nom);
  const libelle = `${NOM_SERVEUR_GOOGLE[s]} : ${action}`;
  return { running: libelle, done: libelle };
}
